import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Icon, Input, Select, Textarea, useToast } from "@astrum/ui";
import { CUSTOM_FIELD_TYPES, MAX_CUSTOM_FIELDS } from "@astrum/shared";
import type { CustomField } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { FIELD_TYPE_LABELS, moved, optionsFromText } from "../lib/custom-fields.js";

interface Props {
  projectId: string;
  /** Project managers and editors; everyone else gets the list read-only. */
  canEdit: boolean;
}

const newOptionId = () => `o${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const optionText = (field: CustomField) => field.options.map((o) => o.label).join("\n");

/**
 * Project settings for custom fields: add, rename, reorder and delete fields
 * and edit a dropdown's options. The type is fixed once created.
 */
export function ProjectFieldsTab({ projectId, canEdit }: Props) {
  const { toast } = useToast();
  const [fields, setFields] = useState<CustomField[] | null>(null);
  const [name, setName] = useState("");
  const [type, setType] = useState<CustomField["type"]>("text");
  const [options, setOptions] = useState("");
  const [busy, setBusy] = useState(false);

  const fail = useCallback((error: unknown, fallback: string) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" }), [toast]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields`)
      .then((res) => !cancelled && setFields(res.fields))
      .catch((e: unknown) => {
        if (!cancelled) {
          setFields([]);
          fail(e, "Could not load the fields.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, fail]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ field: CustomField }>(`/projects/${projectId}/custom-fields`, {
        name,
        type,
        ...(type === "dropdown" ? { options: optionsFromText(options, [], newOptionId) } : {}),
      });
      setFields((prev) => [...(prev ?? []), res.field]);
      setName("");
      setOptions("");
    } catch (e) {
      fail(e, "Could not add the field.");
    } finally {
      setBusy(false);
    }
  };

  const update = async (field: CustomField, body: { name?: string; options?: CustomField["options"] }) => {
    try {
      const res = await api.patch<{ field: CustomField }>(`/projects/${projectId}/custom-fields/${field.id}`, body);
      setFields((prev) => (prev ?? []).map((f) => (f.id === field.id ? res.field : f)));
    } catch (e) {
      fail(e, "Could not save the field.");
    }
  };

  const remove = async (field: CustomField) => {
    if (!window.confirm(`Delete "${field.name}"? The values on every task are deleted with it.`)) return;
    try {
      await api.delete(`/projects/${projectId}/custom-fields/${field.id}`);
      setFields((prev) => (prev ?? []).filter((f) => f.id !== field.id));
    } catch (e) {
      fail(e, "Could not delete the field.");
    }
  };

  const move = async (index: number, by: -1 | 1) => {
    const next = moved(fields ?? [], index, by);
    const previous = fields;
    setFields(next);
    try {
      await api.post(`/projects/${projectId}/custom-fields/reorder`, { fieldIds: next.map((f) => f.id) });
    } catch (e) {
      setFields(previous);
      fail(e, "Could not reorder the fields.");
    }
  };

  if (!fields) return <p className="tv-empty">Loading fields…</p>;
  return (
    <section className="project-detail__section" aria-label="Custom fields">
      <div className="project-detail__section-head">
        <h2>Custom fields</h2>
        <span className="tv-toolbar__count">
          {fields.length} of {MAX_CUSTOM_FIELDS}
        </span>
      </div>
      {fields.length === 0 && (
        <EmptyState
          icon="checklist"
          title="No custom fields"
          description="Add fields such as a budget, a phase or a reviewer. They show on every task and in the Table view."
        />
      )}
      <ul className="project-fields">
        {fields.map((field, index) => (
          <li key={field.id} className="project-fields__row">
            <FieldRow
              field={field}
              canEdit={canEdit}
              first={index === 0}
              last={index === fields.length - 1}
              onRename={(next) => update(field, { name: next })}
              onOptions={(text) => update(field, { options: optionsFromText(text, field.options, newOptionId) })}
              onMove={(by) => move(index, by)}
              onDelete={() => remove(field)}
            />
          </li>
        ))}
      </ul>
      {canEdit && fields.length < MAX_CUSTOM_FIELDS && (
        <form
          className="project-fields__add"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <Input label="Field name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value as CustomField["type"])}>
            {CUSTOM_FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {FIELD_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
          {type === "dropdown" && <Textarea label="Options (one per line)" value={options} onChange={(e) => setOptions(e.target.value)} />}
          <Button type="submit" size="sm" loading={busy} disabled={!name.trim() || (type === "dropdown" && !options.trim())}>
            Add field
          </Button>
        </form>
      )}
    </section>
  );
}

function FieldRow({
  field,
  canEdit,
  first,
  last,
  onRename,
  onOptions,
  onMove,
  onDelete,
}: {
  field: CustomField;
  canEdit: boolean;
  first: boolean;
  last: boolean;
  onRename: (name: string) => void;
  onOptions: (text: string) => void;
  onMove: (by: -1 | 1) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(field.name);
  const [options, setOptions] = useState(optionText(field));
  useEffect(() => setName(field.name), [field.name]);
  useEffect(() => setOptions(optionText(field)), [field]);

  return (
    <div className="project-fields__item">
      <div className="project-fields__line">
        <Input
          aria-label={`Name of ${field.name}`}
          value={name}
          maxLength={60}
          disabled={!canEdit}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name.trim() !== field.name && onRename(name.trim())}
        />
        <span className="project-fields__type">{FIELD_TYPE_LABELS[field.type]}</span>
        {canEdit && (
          <span className="project-fields__actions">
            <Button size="sm" variant="ghost" aria-label={`Move ${field.name} up`} disabled={first} onClick={() => onMove(-1)} icon={<Icon name="chevron-up" size={13} />} />
            <Button size="sm" variant="ghost" aria-label={`Move ${field.name} down`} disabled={last} onClick={() => onMove(1)} icon={<Icon name="chevron-down" size={13} />} />
            <Button size="sm" variant="ghost" aria-label={`Delete ${field.name}`} onClick={onDelete} icon={<Icon name="trash" size={13} />} />
          </span>
        )}
      </div>
      {field.type === "dropdown" && (
        <Textarea
          aria-label={`Options of ${field.name}`}
          value={options}
          disabled={!canEdit}
          onChange={(e) => setOptions(e.target.value)}
          onBlur={() => options !== optionText(field) && onOptions(options)}
        />
      )}
    </div>
  );
}
